import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box } from "@mui/material";
import {
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  Edit,
  Delete,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import RideApi from "Services/Ride";
import RideCreateModal from "Pages/Modals/Ride/Create";
import RideDeleteModal from "Pages/Modals/Ride/Delete";

const Ride = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setbulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [addRideModal, setAddRideModal] = useState(false);
  const [editRideModal, setEditRideModal] = useState(false);
  const [deleteRideModal, setDeleteRideModal] = useState(false);
  const [selectedRideId, setSelectedRideId] = useState("");

  const normalizeRideList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.rides)) {
      return {
        rows: payload?.rides || [],
        total: payload?.total || payload?.rides?.length || 0,
      };
    }
    if (Array.isArray(payload?.data?.data)) {
      return {
        rows: payload?.data?.data || [],
        total: payload?.data?.totalRecords || payload?.data?.data?.length || 0,
      };
    }
    return { rows: [], total: 0 };
  };

  const getRides = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      const normalized = normalizeRideList(res);
      setTotalPages(normalized?.total || 1);
      setRow(normalized?.rows || []);
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    RideApi.getRideList({ page, limit: pageSize, search: searchText }).then(
      onSuccess,
      onFailure
    );
  }, [dispatch, page, pageSize, searchText]);

  const getAllRides = useCallback(() => {
    const onSuccess = (res) => {
      const normalized = normalizeRideList(res);
      setbulkrow(normalized?.rows || []);
    };
    const onFailure = () => {
      setbulkrow([]);
    };
    RideApi.getRideList({ page: 1, limit: "unlimited", search: "" }).then(
      onSuccess,
      onFailure
    );
  }, []);

  useEffect(() => {
    getRides();
    getAllRides();
  }, [getRides, getAllRides]);

  const calculateSno = (pageIndex, pageLimit, index) => {
    return (pageIndex - 1) * pageLimit + index + 1;
  };

  const dataRows = (row || []).map((data, index) => ({
    id: data?.id || index + 1,
    sno: calculateSno(page, pageSize, index),
    rideId: data?.id || "-",
    driverName: `${data?.users?.first_name || ""} ${data?.users?.last_name || ""}`.trim() || "-",
    origin: data?.places_rides_origin_place_idToplaces?.name || "-",
    destination: data?.places_rides_destination_place_idToplaces?.name || "-",
    departureAt: data?.departure_at ? new Date(data?.departure_at).toLocaleString() : "-",
    seats: `${data?.available_seats ?? 0}/${data?.total_seats ?? 0}`,
    price: `${data?.price_per_seat || "0"} ${data?.currency || ""}`.trim(),
    status: data?.status || "-",
    recurring: data?.is_recurring ? "Yes" : "No",
    raw: data,
  }));

  const formatteddatas = dataRows.map((r) => ({
    "S.NO": r?.sno,
    "Ride ID": r?.rideId,
    Driver: r?.driverName,
    Origin: r?.origin,
    Destination: r?.destination,
    Departure: r?.departureAt,
    Seats: r?.seats,
    Price: r?.price,
    Status: r?.status,
    Recurring: r?.recurring,
  }));

  const bulkdataRows = _.map(
    _.orderBy(bulkrow || [], ["created_at"], ["asc"]),
    (data, index) => ({
      sno: index + 1,
      rideId: data?.id || "-",
      driverName: `${data?.users?.first_name || ""} ${data?.users?.last_name || ""}`.trim() || "-",
      origin: data?.places_rides_origin_place_idToplaces?.name || "-",
      destination: data?.places_rides_destination_place_idToplaces?.name || "-",
      departureAt: data?.departure_at ? new Date(data?.departure_at).toLocaleString() : "-",
      seats: `${data?.available_seats ?? 0}/${data?.total_seats ?? 0}`,
      price: `${data?.price_per_seat || "0"} ${data?.currency || ""}`.trim(),
      status: data?.status || "-",
      recurring: data?.is_recurring ? "Yes" : "No",
    })
  );

  const bulkformatteddatas = bulkdataRows.map((r) => ({
    "S.NO": r?.sno,
    "Ride ID": r?.rideId,
    Driver: r?.driverName,
    Origin: r?.origin,
    Destination: r?.destination,
    Departure: r?.departureAt,
    Seats: r?.seats,
    Price: r?.price,
    Status: r?.status,
    Recurring: r?.recurring,
  }));

  const handlePageChange = (newPage) => {
    const theNewPage = parseInt(newPage, 10) + 1;
    if (theNewPage >= 1) setPage(theNewPage);
  };

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleCopy = () => {
    const header = [
      "S.NO",
      "Ride ID",
      "Driver",
      "Origin",
      "Destination",
      "Departure",
      "Seats",
      "Price",
      "Status",
      "Recurring",
    ].join("\t");
    const rows = dataRows
      .map((r) =>
        [
          r?.sno,
          r?.rideId,
          r?.driverName,
          r?.origin,
          r?.destination,
          r?.departureAt,
          r?.seats,
          r?.price,
          r?.status,
          r?.recurring,
        ].join("\t")
      )
      .join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatteddatas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rides");
    XLSX.writeFile(workbook, "RideList.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "RideList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const tableData = dataRows.map((r) => [
      r?.sno,
      r?.rideId,
      r?.driverName,
      r?.origin,
      r?.destination,
      r?.departureAt,
      r?.seats,
      r?.price,
      r?.status,
      r?.recurring,
    ]);
    doc.autoTable({
      head: [["S.NO", "Ride ID", "Driver", "Origin", "Destination", "Departure", "Seats", "Price", "Status", "Recurring"]],
      body: tableData,
    });
    doc.save("RideList.pdf");
  };

  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkformatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All RideList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenAddRide = () => setAddRideModal(true);
  const handleCloseAddRide = (created = false) => {
    setAddRideModal(false);
    if (created) {
      getRides();
      getAllRides();
    }
  };

  const handleEditRide = (rideRow) => {
    const rideId = rideRow?.id || rideRow?._id;
    if (!rideId) {
      dispatch(
        showSnackbar({
          message: "Ride ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedRideId(rideId);
    setEditRideModal(true);
  };

  const handleCloseEditRide = (updated = false) => {
    setEditRideModal(false);
    setSelectedRideId("");
    if (updated) {
      getRides();
      getAllRides();
    }
  };

  const handleDeleteRide = (rideRow) => {
    const rideId = rideRow?.id || rideRow?._id;
    if (!rideId) {
      dispatch(
        showSnackbar({
          message: "Ride ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }

    setSelectedRideId(rideId);
    setDeleteRideModal(true);
  };

  const handleCloseDeleteRide = (deleted = false) => {
    setDeleteRideModal(false);
    setSelectedRideId("");
    if (deleted) {
      getRides();
      getAllRides();
    }
  };

  const columns = [
    { field: "sno", headerName: "S.No", minWidth: 70, sortable: false, disableColumnMenu: true },
    /* { field: "rideId", headerName: "Ride ID", minWidth: 220, sortable: true }, */
    { field: "driverName", headerName: "Driver", minWidth: 150, sortable: true },
    { field: "origin", headerName: "Origin", minWidth: 180, sortable: true },
    { field: "destination", headerName: "Destination", minWidth: 180, sortable: true },
    { field: "departureAt", headerName: "Departure", minWidth: 180, sortable: true },
    { field: "seats", headerName: "Seats", minWidth: 100, sortable: false },
    { field: "price", headerName: "Price/Seat", minWidth: 120, sortable: false },
    {
      field: "status",
      headerName: "Status",
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const rawStatus = String(params?.row?.status || "").toUpperCase();
        const statusLabel = rawStatus || "UNKNOWN";
        const statusBg =
          rawStatus === "CANCELLED"
            ? "#E53935"
            : rawStatus === "PICKEDUP"
            ? "#0DA5C0"
            : "#4CAF50";

        return (
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: `${statusBg} !important`,
              color: "#fff !important",
              borderRadius: "6px",
              fontWeight: 700,
              fontSize: 11,
              minWidth: 92,
              padding: "4px 10px",
              "&:hover": {
                backgroundColor: `${statusBg} !important`,
                opacity: 0.95,
              },
            }}
          >
            {statusLabel}
          </Button>
        );
      },
    },
    { field: "recurring", headerName: "Recurring", minWidth: 100, sortable: true },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            style={{ minWidth: 36, padding: "4px 8px" }}
            onClick={() => handleEditRide(params?.row?.raw)}
          >
            <Edit fontSize="small" />
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            style={{ minWidth: 36, padding: "4px 8px", backgroundColor: "#e5393526" }}
            onClick={() => handleDeleteRide(params?.row?.raw)}
          >
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}>
        <Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>
          COPY
        </Button>
      </Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}>
        <Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>
          EXCEL
        </Button>
      </Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}>
        <Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>
          CSV
        </Button>
      </Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}>
        <Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>
          PDF
        </Button>
      </Grid>
      <Grid item xs={2} md={2} lg={2} xl={2} className={classes.funBtn}>
        <Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>
          Download ALL
        </Button>
      </Grid>
      <Grid item xs={6} md={6} lg={6} xl={6} className={classes.addBtn}>
        <Button variant="contained" onClick={handleOpenAddRide}>
          Add Ride
        </Button>
      </Grid>

      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            onPageChange={(newPage) => handlePageChange(newPage)}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalPages}
            filterMode="server"
            onFilterModelChange={(model) => {
              const value = model?.quickFilterValues?.join(" ") || "";
              setSearchText(value);
            }}
            density="compact"
            components={{ Toolbar: CustomToolbar }}
          />
        </section>
      </Grid>
    </Grid>
    <RideCreateModal openDialog={addRideModal} handleClose={handleCloseAddRide} />
    <RideCreateModal openDialog={editRideModal} handleClose={handleCloseEditRide} rideId={selectedRideId} />
    <RideDeleteModal openDialog={deleteRideModal} handleClose={handleCloseDeleteRide} rideId={selectedRideId} />
    </>
  );
};

export default Ride;
